// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Star Wars Universe - Star Systems Configuration
 * 
 * A galaxy far, far away with iconic locations from the Star Wars saga.
 */

export const starSystems = [
  {
    id: 800,
    name: 'Coruscant',
    type: 'station',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 }, // Visual center
    threat: 'SAFE',
    resources: 'URBAN WORLD',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#4a5568',
    secondaryColor: '#f7fafc',
    hasRings: false,
    description: 'The galactic capital. Ecumenopolis covered entirely in cityscape. Center of Republic/Empire politics and trade.',
    scanDescription: 'Massive urban planet. Trillions of inhabitants. Advanced technology. Full services available.'
  },
  {
    id: 801,
    name: 'Tatooine',
    type: 'planet',
    distance: 15,
    coordinates: { x: 60, y: 55, z: 3 },
    threat: 'MODERATE',
    resources: 'LOW',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'remote',
    primaryColor: '#d4a574',
    secondaryColor: '#f4e4c1',
    hasRings: false,
    description: 'Desert planet with twin suns. Home to moisture farmers, Jawas, and Tusken Raiders. Hive of scum and villainy.',
    scanDescription: 'Harsh desert world. Binary star system. Sparse population. Moisture farming. Criminal activity detected.'
  },
  {
    id: 802,
    name: 'Hoth',
    type: 'planet',
    distance: 22,
    coordinates: { x: 30, y: 45, z: -5 },
    threat: 'HIGH',
    resources: 'MEDIUM',
    services: ['refuel', 'exploration'],
    tradeType: 'remote',
    primaryColor: '#e0f2fe',
    secondaryColor: '#bae6fd',
    hasRings: false,
    description: 'Frozen ice planet. Former Rebel Alliance base location. Extreme cold and dangerous wildlife.',
    scanDescription: 'Ice world. Sub-zero temperatures. Wampa creatures detected. Abandoned rebel base present.'
  },
  {
    id: 803,
    name: 'Endor',
    type: 'planet',
    distance: 18,
    coordinates: { x: 52, y: 65, z: 8 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'trade'],
    tradeType: 'agricultural',
    primaryColor: '#22c55e',
    secondaryColor: '#86efac',
    hasRings: false,
    description: 'Forest moon. Home to the Ewok species. Site of the second Death Star battle.',
    scanDescription: 'Lush forest moon. Primitive Ewok civilization. Rich biodiversity. Peaceful environment.'
  },
  {
    id: 804,
    name: 'Naboo',
    type: 'planet',
    distance: 20,
    coordinates: { x: 38, y: 58, z: -3 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'luxury',
    primaryColor: '#3b82f6',
    secondaryColor: '#93c5fd',
    hasRings: false,
    description: 'Beautiful planet with vast oceans and green plains. Home to humans and Gungans. Queen Amidala\'s homeworld.',
    scanDescription: 'Peaceful world. Advanced human civilization. Underwater Gungan cities. Rich culture and art.'
  },
  {
    id: 805,
    name: 'Mustafar',
    type: 'planet',
    distance: 28,
    coordinates: { x: 65, y: 40, z: -8 },
    threat: 'EXTREME',
    resources: 'MEDIUM',
    services: ['refuel', 'exploration'],
    tradeType: 'mining',
    primaryColor: '#dc2626',
    secondaryColor: '#f97316',
    hasRings: false,
    description: 'Volcanic planet with lava rivers. Site of Anakin and Obi-Wan\'s duel. Extremely dangerous.',
    scanDescription: 'Volcanic hellscape. Lava flows detected. Extreme heat. Vader\'s fortress present. Approach with caution.'
  },
  {
    id: 806,
    name: 'Dagobah',
    type: 'planet',
    distance: 25,
    coordinates: { x: 28, y: 62, z: 5 },
    threat: 'MODERATE',
    resources: 'LOW',
    services: ['refuel', 'exploration'],
    tradeType: 'remote',
    primaryColor: '#166534',
    secondaryColor: '#4ade80',
    hasRings: false,
    description: 'Swamp planet. Former home of Jedi Master Yoda. Strong in the Force. Dense fog and dangerous creatures.',
    scanDescription: 'Murky swamp world. Dense atmosphere. Strong Force presence. Minimal technology. Yoda\'s hut detected.'
  },
  {
    id: 807,
    name: 'Kamino',
    type: 'planet',
    distance: 30,
    coordinates: { x: 48, y: 35, z: -10 },
    threat: 'MODERATE',
    resources: 'MEDIUM',
    services: ['refuel', 'repairs', 'trade'],
    tradeType: 'industrial',
    primaryColor: '#0ea5e9',
    secondaryColor: '#7dd3fc',
    hasRings: false,
    description: 'Ocean planet with constant rain. Home to Kaminoan cloners. Birthplace of the Clone Army.',
    scanDescription: 'Water world. Perpetual storms. Advanced cloning facilities. Kaminoan civilization. Military presence.'
  },
  {
    id: 808,
    name: 'Alderaan Memorial',
    type: 'station',
    distance: 12,
    coordinates: { x: 55, y: 48, z: 2 },
    threat: 'SAFE',
    resources: 'MEMORIAL',
    services: ['refuel', 'repairs', 'trade'],
    tradeType: 'luxury',
    primaryColor: '#8b5cf6',
    secondaryColor: '#c4b5fd',
    hasRings: true,
    description: 'Memorial station at the site of Alderaan\'s destruction. Asteroid field remains. Place of remembrance.',
    scanDescription: 'Asteroid field. Former location of Alderaan. Memorial station. Peaceful. No weapons fire permitted.'
  },
  {
    id: 809,
    name: 'Death Star Ruins',
    type: 'station',
    distance: 35,
    coordinates: { x: 70, y: 58, z: 12 },
    threat: 'HIGH',
    resources: 'WRECKAGE',
    services: ['refuel'],
    tradeType: 'remote',
    primaryColor: '#1f2937',
    secondaryColor: '#6b7280',
    hasRings: false,
    description: 'Debris field of the destroyed Death Star II. Dangerous wreckage. Imperial remnants may be present.',
    scanDescription: 'Massive debris field. Radiation detected. Imperial technology fragments. Scavengers active. Dangerous.'
  },
  {
    id: 810,
    name: 'Bespin',
    type: 'station',
    distance: 24,
    coordinates: { x: 42, y: 42, z: -6 },
    threat: 'MODERATE',
    resources: 'TIBANNA GAS',
    services: ['refuel', 'repairs', 'trade', 'weapons'],
    tradeType: 'mining',
    primaryColor: '#f59e0b',
    secondaryColor: '#fbbf24',
    hasRings: false,
    description: 'Gas giant with Cloud City mining colony. Tibanna gas extraction. Lando Calrissian\'s former domain.',
    scanDescription: 'Gas giant. Cloud City floating platform. Tibanna gas mining. Luxury accommodations. Trade hub.'
  },
  {
    id: 811,
    name: 'Jakku',
    type: 'planet',
    distance: 32,
    coordinates: { x: 25, y: 55, z: -12 },
    threat: 'MODERATE',
    resources: 'LOW',
    services: ['refuel', 'exploration', 'trade'],
    tradeType: 'remote',
    primaryColor: '#d97706',
    secondaryColor: '#fbbf24',
    hasRings: false,
    description: 'Desert junkyard planet. Site of major Imperial-Rebel battle. Scavengers pick through wreckage.',
    scanDescription: 'Desert wasteland. Massive starship graveyard. Scavenger settlements. Harsh conditions. Limited resources.'
  }
];

export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});

const starwarsSystems = { starSystems, starPositions };
export default starwarsSystems;
